import { isInternalUserEmail } from '@common/utils/internalEmail';
import { useCheckPermission } from '~/common/hooks/useCheckPermission';
import { useAppContext } from '~/services/main/contexts/AppContext';
import {
  FeatureKey,
  useIsFeatureEnabledForPayload,
} from './useIsFeatureEnabledForPayload';

interface OrganizationComplianceDashboardAccess {
  canViewOrganizationComplianceDashboard: boolean;
  isCheckingAccess: boolean;
}

/**
 * Resolves whether the current user can access the production compliance
 * dashboard for the selected organization.
 */
export const useCanViewOrganizationComplianceDashboard =
  (): OrganizationComplianceDashboardAccess => {
    const {
      user,
      selectedOrganization,
      isSelectedOrgAdmin,
      loadingOrganizations,
      loadingIsSelectedOrgAdmin,
    } = useAppContext();
    const isInternalUser = isInternalUserEmail(user?.email);
    const {
      isAuthorized: isSelectedOrgOwnerOrCoOwner,
      loading: loadingIsSelectedOrgOwnerOrCoOwner,
    } = useCheckPermission(
      'accessControl.isOwnerOrCoOwner',
      {
        skip: !selectedOrganization || isInternalUser,
      },
      {
        organizationUuid: selectedOrganization?.id,
      }
    );
    const isComplianceDashboardEnabled = useIsFeatureEnabledForPayload({
      featureFlagKey: FeatureKey.ORG_COMPLIANCE_DASHBOARD,
      organizationId: selectedOrganization?.id,
    });
    const isCheckingAccess =
      loadingOrganizations ||
      loadingIsSelectedOrgAdmin ||
      loadingIsSelectedOrgOwnerOrCoOwner ||
      isComplianceDashboardEnabled === undefined;

    // TODO: Recover admin access when per-office dashboard permissions are solved.
    const canViewOrganizationComplianceDashboard = Boolean(
      selectedOrganization &&
        isSelectedOrgAdmin &&
        (isInternalUser || isSelectedOrgOwnerOrCoOwner) &&
        isComplianceDashboardEnabled === true
    );

    return {
      canViewOrganizationComplianceDashboard,
      isCheckingAccess,
    };
  };
